import StatusPill from "../../components/UI/StatusPill";
import { Link } from "react-router";
import {
  iconTypeMap,
  textColor,
  pillColor,
  tooltipTitle,
  cellStyleMap,
} from "./DosingTrackerMapping";
import {
  COLUMN_WIDTHS,
  COLUMNS_WITH_LINK,
  STATUS_COLUMNS,
  NO_TIMEPOINT_COLUMNS,
  TOTAL_MIN_EXPECTED_DOSE,
  TOTAL_MAX_EXPECTED_DOSE,
} from "./DosingConstants";

export const getCellRenderer = (
  text: string | null,
  header: string,
  path: string,
  type: string,
  record: Record<string, unknown>,
) => {
  if (STATUS_COLUMNS.has(header)) {
    return (
      <StatusPill
        text={text}
        tooltipTitle={tooltipTitle}
        pillColor={pillColor}
        textColor={textColor}
      />
    );
  }

  if (COLUMNS_WITH_LINK.has(header)) {
    return (
      <Link
        style={{ color: "#06709B" }}
        to={`${path}/${(record as Record<string, string>).subject}/study-intervention`}
      >
        {text}
      </Link>
    );
  }
  // TIMEPOINTS: Check if the text is one of the keys in the iconTypeMap
  if (
    (text === null ||
      isNaN(text as unknown as number) ||
      Object.hasOwn(iconTypeMap, text)) &&
    header !== "study_arm_label"
  ) {
    return iconTypeMap[text as string] || iconTypeMap["Not Required"];
  }

  if (!isNaN(Number(text)) && header !== "site") {
    return parseFloat(text as string).toFixed(2);
  }

  return text;
};

const convertToNumber = (text: string | null): number => {
  if (
    text === "Data Pending" ||
    text === "Data Missing" ||
    text === "Data Discrepancy" ||
    text === "Not Required" ||
    text === "-"
  ) {
    return -1;
  }
  return parseFloat(text as string);
};

export const getCellStyle = (
  text: string | null,
  type: string,
  header: string,
  record?: Record<string, unknown>,
) => {
  if (header !== "total_dosage") {
    if (type === "status") {
      //TIMEPOINT HEADERS  in DOSE STATUS
      return cellStyleMap[text as string] || cellStyleMap["default"];
    }
    // TIMEPOINT HEADERS in DOSE ADMINISTRATION
    const number = convertToNumber(text);
    if (isNaN(number)) {
      return cellStyleMap["default"];
    }
    if (number > 1) {
      return cellStyleMap["Missed"];
    }
    if (number === 1) {
      return cellStyleMap["Administered"];
    }
    if ((number > 0 && number < 1) || number === 0) {
      return cellStyleMap["Reduced"];
    }
    if (number === -1) {
      return cellStyleMap[text as string];
    }
  } else {
    // CELL STYLES FOR TOTAL DOSAGE COLUMN
    const number = convertToNumber(text);
    if (number > TOTAL_MAX_EXPECTED_DOSE) {
      // The total dosage style will apply only to all the values greater than the max expected dose
      return cellStyleMap["Missed"]; //red color
    }
    if (record && record["study_status"] !== "Active") {
      // The total dosage style will apply only if the study status is different than active

      if (
        number >= TOTAL_MIN_EXPECTED_DOSE &&
        number <= TOTAL_MAX_EXPECTED_DOSE
      ) {
        return cellStyleMap["Administered"]; //blue color
      }
      if (number < TOTAL_MIN_EXPECTED_DOSE) {
        return cellStyleMap["Reduced"]; //orange color
      }
    }
  }
};

export const getCellProperties = (
  record: Record<string, unknown>,
  header: string,
  handleCellClick: (
    header: string,
    value: unknown,
    record: Record<string, unknown>,
  ) => void,
  type: string,
) => {
  if (
    NO_TIMEPOINT_COLUMNS.has(header) ||
    (header === "total_dosage" && type === "status")
  ) {
    return {};
  }
  if (header === "total_dosage" && type === "value") {
    return {
      style: getCellStyle(record[header] as string, type, header, record),
    };
  }
  return {
    className: `cell-highlight`,
    style: getCellStyle(record[header] as string, type, header),
    onClick: () =>
      handleCellClick(
        header,
        record[header] === "-" ? "Not Required" : record[header],
        record,
      ),
  };
};

export const getCellWidth = (header: string): number => {
  return COLUMN_WIDTHS[header] || COLUMN_WIDTHS.DEFAULT;
};
